// One-click permission bundles for a role (Settings → Permissions → Roles).
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, FileBarChart, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { roleEffective } from "@/lib/access";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function RolePresets({ roles, audiences, canManage, onApplied }) {
  const [roleId, setRoleId] = useState("");
  const [preset, setPreset] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const role = roles.find((r) => r.role_id === roleId);
  const effective = useMemo(() => (role ? roleEffective(role, audiences) : new Set()), [role, audiences]);

  useEffect(() => {
    if (!role) { setPreset(null); return; }
    api.get("/roles/presets", { params: { base_role: role.base_role } }).then((r) => {
      const p = r.data.presets[0];
      setPreset(p);
      setPicked(new Set(p.items.filter((i) => i.default).map((i) => i.perm)));
    }).catch(() => setPreset(null));
  }, [role?.role_id, role?.base_role]);     // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (perm, on) => setPicked((prev) => { const n = new Set(prev); if (on) n.add(perm); else n.delete(perm); return n; });
  const newGrants = preset ? preset.items.filter((i) => picked.has(i.perm) && !effective.has(i.perm)) : [];

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/roles/${roleId}/presets/${preset.id}`, { perms: [...picked] });
      toast.success(data.added.length
        ? `${role.name}: granted ${data.added.join(", ")}`
        : `${role.name} already held everything selected`);
      onApplied?.(data.role);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not apply the preset"); }
    finally { setBusy(false); }
  };

  if (!canManage) return null;
  const candidates = roles.filter((r) => r.role_id !== "super_admin")
    .sort((a, b) => (a.base_role === "partner" ? 0 : 1) - (b.base_role === "partner" ? 0 : 1) || a.name.localeCompare(b.name));

  return (
    <Card className="mb-4 border-slate-200 p-5 shadow-sm" data-testid="role-presets-card">
      <div className="flex flex-wrap items-start gap-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#eef2fb] text-primary"><Wand2 className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-sm font-semibold text-slate-800">Presets · Reports access</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Give a role the Reports tab plus the feature permissions behind the reports open to it, in one click.
            Reports stay scoped to each person's own data unless you also tick “All data”.
          </p>
        </div>
        <div className="w-64 space-y-1.5">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Role</Label>
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger className="h-9" data-testid="preset-role-select"><SelectValue placeholder="Pick a role…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {candidates.map((r) => (
                <SelectItem key={r.role_id} value={r.role_id} data-testid={`preset-role-${r.role_id}`}>
                  {r.name} <span className="text-muted-foreground">· {r.base_role}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {role && !preset && <div className="mt-4 text-sm text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /></div>}

      {role && preset && (
        <div className="mt-4 space-y-3" data-testid="preset-items">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {preset.items.map((it) => {
              const held = effective.has(it.perm);
              return (
                <label key={it.perm} className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm ${
                  it.warning ? "border-amber-200 bg-amber-50/50" : "border-slate-200 bg-white"}`} data-testid={`preset-item-${it.perm}`}>
                  <Checkbox className="mt-0.5" checked={picked.has(it.perm)} disabled={it.required}
                            onCheckedChange={(v) => toggle(it.perm, !!v)} data-testid={`preset-check-${it.perm}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-800">{it.label}</span>
                      <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-500">{it.perm}</code>
                      {held && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`preset-held-${it.perm}`}><Check className="h-3 w-3" /> already has</span>}
                      {it.warning && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> {it.warning}</span>}
                    </span>
                    <span className="block text-xs text-slate-500">Unlocks: {it.unlocks.join(" · ")}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-slate-500" data-testid="preset-summary">
              {newGrants.length
                ? `${newGrants.length} new grant${newGrants.length === 1 ? "" : "s"} for ${role.name}: ${newGrants.map((i) => i.perm).join(", ")}`
                : `${role.name} already holds everything selected.`}
            </span>
            <Button onClick={apply} disabled={busy || !picked.size} data-testid="preset-apply">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileBarChart className="h-4 w-4" />} Apply to {role.name}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
